import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, CreditCard, Download, FileText } from 'lucide-react';
import { subscriptionAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export default function SubscriptionPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const queryClient = useQueryClient();
  const { studio } = useAuth();
  const studioId = studio?.id;

  const { data: subscription } = useQuery({
    queryKey: ['subscription', studioId],
    queryFn: () => subscriptionAPI.getSubscription(studioId),
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  const { data: tiers } = useQuery({
    queryKey: ['subscription-tiers'],
    queryFn: () => subscriptionAPI.getTiers(),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  const { data: paymentMethods } = useQuery({
    queryKey: ['payment-methods', studioId],
    queryFn: () => subscriptionAPI.getPaymentMethods(studioId),
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  const { data: invoices } = useQuery({
    queryKey: ['invoices', studioId],
    queryFn: () => subscriptionAPI.getInvoices(studioId, { limit: 10 }),
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  const updateTierMutation = useMutation({
    mutationFn: (tier) => subscriptionAPI.updateTier(studioId, tier),
    onSuccess: () => {
      queryClient.invalidateQueries(['subscription', studioId]);
      toast.success('Subscription tier updated!');
    }
  });

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'billing', label: 'Billing' },
    { id: 'invoices', label: 'Invoices' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[var(--text-primary)]">Subscription & Billing</h1>
        <p className="text-[var(--text-secondary)] mt-2">Manage your subscription and payment information</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--border-subtle)]">
        <div className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-4 border-b-2 font-semibold transition-all duration-300 ${
                activeTab === tab.id
                  ? 'border-[var(--primary)] text-[var(--text-secondary)] shadow-sm'
                  : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--primary)]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Current Plan */}
          <div className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-primary-200">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Current Plan</h2>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-[var(--text-secondary)] capitalize group-hover:text-[var(--text-secondary)] transition-colors">
                  {subscription?.data?.subscription?.tier || 'Basic'} Plan
                </h3>
                <p className="text-[var(--text-secondary)] mt-1 group-hover:text-[var(--text-secondary)] transition-colors">
                  ${subscription?.data?.subscription?.monthlyPrice || 0}/month
                </p>
                <p className="text-sm text-[var(--text-muted)] mt-2">
                  Status: <span className={`font-medium ${
                    subscription?.data?.subscription?.status === 'active' ? 'text-green-600' :
                    subscription?.data?.subscription?.status === 'trial' ? 'text-[var(--text-secondary)]' :
                    'text-red-600'
                  }`}>
                    {subscription?.data?.subscription?.status || 'trial'}
                  </span>
                </p>
              </div>
              <button className="btn btn-outline shadow-md hover:shadow-lg transition-all duration-300">Change Plan</button>
            </div>
          </div>

          {/* Available Tiers */}
          <div>
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Available Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tiers?.data?.tiers && Object.entries(tiers.data.tiers).map(([tierKey, tierInfo]) => {
                const isCurrent = tierKey === subscription?.data?.subscription?.tier;
                const tierName = tierKey.charAt(0).toUpperCase() + tierKey.slice(1);
                return (
                  <div
                    key={tierKey}
                    className={`card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 ${
                      isCurrent
                        ? 'border-primary-400 shadow-lg scale-105'
                        : 'border-transparent hover:border-primary-200'
                    }`}
                    style={{ background: isCurrent ? 'var(--bg-elevated)' : 'var(--bg-surface)' }}
                  >
                    {isCurrent && (
                      <div
                        className="text-white text-sm font-bold px-4 py-2 rounded-full inline-block mb-4 shadow-lg border-2"
                        style={{
                          backgroundColor: 'var(--primary)',
                          borderColor: 'var(--primary-hover)'
                        }}
                      >
                        ✓ Current Plan
                      </div>
                    )}
                    
                    <h3 className="text-2xl font-bold text-[var(--text-primary)] group-hover:text-[var(--text-secondary)] transition-colors">{tierName}</h3>
                    <div className="mt-4 mb-6">
                      <span className="text-4xl font-bold text-[var(--text-primary)] group-hover:text-[var(--text-secondary)] transition-colors">${tierInfo.monthlyPrice}</span>
                      <span className="text-[var(--text-secondary)] group-hover:text-[var(--text-secondary)] transition-colors">/month</span>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {tierInfo.features?.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => updateTierMutation.mutate(tierKey)}
                      disabled={isCurrent || updateTierMutation.isPending}
                      className={`btn w-full shadow-md hover:shadow-lg transition-all duration-300 ${
                        isCurrent 
                          ? 'btn-secondary cursor-not-allowed opacity-75' 
                          : 'btn-primary'
                      }`}
                    >
                      {isCurrent ? 'Current Plan' : 'Select Package'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="space-y-6">
          {/* Payment Methods */}
          <div className="card group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-primary-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[var(--text-primary)]">Payment Methods</h2>
              {/* FEATURE_DISABLED: Add Payment Method / Connect to Stripe button - re-enable when ready */}
              {/* <button className="btn btn-primary shadow-md hover:shadow-lg transition-all duration-300">
                <CreditCard className="w-5 h-5 mr-2" />
                Add Payment Method
              </button> */}
            </div>

            {paymentMethods?.data?.paymentMethods?.length > 0 ? (
              <div className="space-y-3">
                {paymentMethods.data.paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg border border-[var(--border-subtle)] hover:bg-secondary-100 hover:border-[var(--primary)] hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-8 bg-gradient-to-r from-primary-600 to-primary-800 rounded flex items-center justify-center shadow-sm">
                        <CreditCard className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-[var(--text-primary)] capitalize">
                          {method.brand} •••• {method.last4}
                        </p>
                        <p className="text-sm text-[var(--text-secondary)]">
                          Expires {method.expiryMonth}/{method.expiryYear}
                        </p>
                      </div>
                    </div>
                    {method.isDefault && (
                      <span className="text-sm bg-green-100 text-green-700 px-3 py-1.5 rounded-full font-semibold shadow-sm">
                        Default
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CreditCard className="w-16 h-16 text-primary-300 mx-auto mb-4" />
                <p className="text-[var(--text-muted)]">No payment methods added</p>
              </div>
            )}
          </div>

          {/* Billing Information */}
          <div className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-primary-200">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Billing Information</h2>
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-[var(--border-subtle)] group-hover:border-primary-200 transition-colors">
                <span className="text-[var(--text-secondary)] group-hover:text-[var(--text-secondary)] transition-colors">Next billing date</span>
                <span className="font-semibold text-[var(--text-primary)] group-hover:text-[var(--text-primary)] transition-colors">
                  {subscription?.data?.subscription?.currentPeriodEnd
                    ? format(new Date(subscription.data.subscription.currentPeriodEnd), 'MMMM d, yyyy')
                    : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-[var(--border-subtle)] group-hover:border-primary-200 transition-colors">
                <span className="text-[var(--text-secondary)] group-hover:text-[var(--text-secondary)] transition-colors">Billing amount</span>
                <span className="font-semibold text-[var(--text-primary)] group-hover:text-[var(--text-primary)] transition-colors">
                  ${subscription?.data?.subscription?.monthlyPrice || 0}/month
                </span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-[var(--text-secondary)] group-hover:text-[var(--text-secondary)] transition-colors">Auto-renewal</span>
                <span className="font-semibold text-green-600">Enabled</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="card group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-primary-200">
          <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Invoice History</h2>
          
          {invoices?.data?.invoices?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-secondary-50 to-primary-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-secondary)]">Invoice #</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-secondary)]">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-secondary)]">Amount</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-secondary)]">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-secondary)]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200">
                  {invoices.data.invoices.map((invoice) => (
                    <tr 
                      key={invoice.id}
                      className="hover:bg-primary-50 transition-colors duration-200"
                    >
                      <td className="px-4 py-4 text-sm font-semibold text-[var(--text-primary)]">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-4 py-4 text-sm text-[var(--text-secondary)]">
                        {format(new Date(invoice.invoiceDate), 'MMM d, yyyy')}
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-[var(--text-primary)]">
                        ${invoice.total}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`text-xs px-3 py-1.5 rounded-full font-semibold shadow-sm ${
                          invoice.status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : invoice.status === 'open'
                            ? 'bg-primary-100 text-[var(--text-secondary)]'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <button className="text-[var(--text-secondary)] hover:text-[var(--text-secondary)] text-sm font-semibold flex items-center gap-2 hover:gap-3 transition-all duration-200 shadow-sm hover:shadow-md px-3 py-1.5 rounded-lg hover:bg-primary-50">
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-primary-300 mx-auto mb-4" />
              <p className="text-[var(--text-muted)]">No invoices yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
